'use client'

import { useEffect, useState } from 'react'

import { useQuizAttempt, useStartQuizAttempt } from '../hooks/useQuizAttempt'
import { useQuiz } from '../hooks/useQuizzes'
import { QUIZ_PAGE_VARIANT } from '../lib/quiz-ui'

import { Card, CardBody, CardHeader, CardTitle } from '@/shared/ui/Card'
import { Page } from '@/shared/ui/Page'
import { StatusScreen } from '@/shared/ui/StatusScreen'

import { QuizAttemptRunner } from './QuizAttemptRunner'
import { QuizIntro } from './QuizIntro'

interface QuizDetailPageProps {
  quizId: number
}

const Skeleton = () => (
  <div className="h-40 w-full animate-pulse rounded-xl bg-white/40" />
)

export function QuizDetailPage({ quizId }: QuizDetailPageProps) {
  const { data: quiz, isLoading, isError } = useQuiz(quizId)
  const start = useStartQuizAttempt(quizId)

  // A tentativa em curso nesta tela. Uma vez travada aqui, NÃO é solta: ao
  // finalizar, o backend deixa de oferecer retomada, e sem a trava o aluno
  // seria jogado de volta para a introdução em vez de ver o próprio resultado.
  const [attemptId, setAttemptId] = useState<number | null>(null)

  const resumableId = quiz?.resumable_attempt_id ?? null

  useEffect(() => {
    if (attemptId === null && resumableId !== null) setAttemptId(resumableId)
  }, [attemptId, resumableId])

  const { data: attempt } = useQuizAttempt(attemptId)

  if (isLoading) {
    return (
      <Page variant={QUIZ_PAGE_VARIANT}>
        <Card variant={QUIZ_PAGE_VARIANT} className="w-full max-w-2xl">
          <CardBody>
            <Skeleton />
          </CardBody>
        </Card>
      </Page>
    )
  }

  // Quiz indisponível é OMITIDO pelo backend (não existe estado bloqueado para
  // quiz), então 404 aqui significa "não existe ou não é seu" — a mesma
  // resposta para o aluno.
  if (isError || !quiz) {
    return (
      <StatusScreen
        variant={QUIZ_PAGE_VARIANT}
        title="Quiz indisponível"
        description="Este quiz não está disponível para você agora."
      />
    )
  }

  const running = attemptId !== null

  return (
    <Page variant={QUIZ_PAGE_VARIANT}>
      <div className="flex w-full min-w-0 max-w-full flex-col items-center gap-4 px-2 sm:px-0">
        <Card variant={QUIZ_PAGE_VARIANT} className="w-full max-w-2xl">
          <CardHeader>
            <CardTitle>{quiz.name}</CardTitle>
          </CardHeader>
          <CardBody>
            {running ? (
              // A tentativa retomada só chega no request seguinte; a iniciada
              // agora já vem do cache da mutation, sem piscar.
              attempt ? (
                <QuizAttemptRunner key={attempt.id} initialAttempt={attempt} />
              ) : (
                <Skeleton />
              )
            ) : (
              <QuizIntro
                quiz={quiz}
                isStarting={start.isPending}
                errorMessage={
                  start.isError
                    ? 'Não foi possível iniciar. Você pode já ter usado todas as tentativas deste quiz.'
                    : null
                }
                onStart={() =>
                  start.mutate(undefined, {
                    onSuccess: result => setAttemptId(result.id)
                  })
                }
              />
            )}
          </CardBody>
        </Card>
      </div>
    </Page>
  )
}
